import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import {
  CloudUpload,
  CheckCircle2,
  AlertTriangle,
  Lock,
} from "lucide-react";

import { useOfflineStatus } from "../hooks/useOnlineSync";

// SyncStatus — floating confirmation pill at bottom center.
// OfflineBanner (top) handles the persistent offline/syncing/pending states.
// This pill handles: (1) brief "Sincronizado" success flash, (2) error action.
export default function SyncStatus() {
  const { pathname } = useLocation();

  const {
    isOnline = true,
    outboxCount = 0,
    outboxStatus = {},
    isSyncing = false,
    sessionExpired = false,
    sessionExpiredCount = 0,
    retryFailed,
  } = useOfflineStatus() || {};

  const [showSynced, setShowSynced] = useState(false);
  const [visible, setVisible]       = useState(false);
  const initialRenderRef            = useRef(true);

  const hasErrors  = (outboxStatus?.failed || 0) > 0;
  const isPending  = outboxCount > 0 && !isSyncing && !hasErrors;

  const isSynced =
    isOnline &&
    !isSyncing &&
    outboxCount === 0 &&
    !hasErrors &&
    !sessionExpired;

  /* ── Auto-hide success flash ───────────────────────────────────────────── */

  useEffect(() => {
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      return;
    }
    if (isSynced) {
      setShowSynced(true);
      const t = setTimeout(() => setShowSynced(false), 3000);
      return () => clearTimeout(t);
    }
    setShowSynced(false);
  }, [isSynced]);

  /* ── Animate in/out ────────────────────────────────────────────────────── */

  const shouldShow =
    hasErrors ||
    (sessionExpired && sessionExpiredCount > 0) ||
    (isPending && isOnline) ||
    showSynced;

  useEffect(() => {
    // Small delay before hiding so the exit animation has time to run
    let t;
    if (shouldShow) {
      setVisible(true);
    } else {
      t = setTimeout(() => setVisible(false), 250);
    }
    return () => clearTimeout(t);
  }, [shouldShow]);

  if (!visible || pathname.startsWith("/member")) return null;

  /* ── Config ────────────────────────────────────────────────────────────── */

  let config = {
    color:  "bg-emerald-50/95 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800/50",
    icon:   <CheckCircle2 size={13} strokeWidth={2} />,
    text:   "Sincronizado",
    action: null,
  };

  if (hasErrors) {
    config = {
      color:  "bg-rose-50/95 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-800/50",
      icon:   <AlertTriangle size={13} strokeWidth={2} />,
      text:   `No se pudo sincronizar${(outboxStatus?.failed || 0) > 1 ? ` (${outboxStatus.failed})` : ""}`,
      action: retryFailed,
    };
  } else if (sessionExpired && sessionExpiredCount > 0) {
    config = {
      color:  "bg-amber-50/95 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800/50",
      icon:   <Lock size={13} strokeWidth={2} />,
      text:   `Sesión vencida · ${sessionExpiredCount} ${sessionExpiredCount === 1 ? "cambio" : "cambios"} en espera`,
      action: null,
    };
  } else if (isPending && isOnline) {
    config = {
      color:  "bg-slate-50/95 dark:bg-slate-800/80 text-slate-500 dark:text-slate-400 border-slate-200/80 dark:border-slate-700/50",
      icon:   <CloudUpload size={13} strokeWidth={2} />,
      text:   `${outboxCount} ${outboxCount === 1 ? "cambio" : "cambios"} por sincronizar`,
      action: null,
    };
  }

  /* ── Render ────────────────────────────────────────────────────────────── */

  return (
    <div
      className="
        fixed bottom-24 left-1/2 -translate-x-1/2
        z-50
        pointer-events-none
        transition-all duration-200 ease-out
      "
      style={{
        opacity:   shouldShow ? 1 : 0,
        transform: `translateX(-50%) translateY(${shouldShow ? "0" : "4px"}) scale(${shouldShow ? 1 : 0.97})`,
      }}
    >
      <div
        className={`
          flex items-center gap-1.5
          px-3 py-1.5
          rounded-full border
          shadow-[0_2px_12px_rgba(0,0,0,0.08)]
          backdrop-blur-md
          pointer-events-auto
          transition-all duration-200 ease-out
          ${config.action ? "cursor-pointer active:scale-[0.98]" : "cursor-default"}
          ${config.color}
        `}
        onClick={() => config.action?.()}
      >
        {config.icon}

        <span className="text-[11px] font-semibold leading-none whitespace-nowrap">
          {config.text}
        </span>

        {config.action && (
          <span className="text-[10px] font-medium underline underline-offset-2 ml-0.5 opacity-80">
            Reintentar
          </span>
        )}
      </div>
    </div>
  );
}
